// Which accounts a basket row can land on, and which of them it is sent for.
// Kept free of hooks and the API layer so it runs in the Node test environment.

import type { MFMember } from "state/members/member";
import type { FlatRequestItem } from "./types";

export interface IdentityOption {
  /** MFIdentity.id — the value sent to the backend as `identityId`. */
  id: string;
  /** The account handle the grant lands on — what the user recognises. */
  label: string;
  /** Secondary line (the external id when a username is the primary label). */
  subtitle?: string;
  /** Integration logo — shared across a person's accounts on that integration. */
  imageUrl?: string;
  /**
   * This account already holds the requested access. Backend-supplied:
   * `MFIdentity` carries no per-resource access status today, so this stays
   * `false` until the API provides it. The UI is already wired to disable
   * such rows and default away from them.
   */
  alreadyHasAccess?: boolean;
  /**
   * A non-human identity the member owns (from `requestableNhis`). Unlike a lone
   * human identity, a lone NHI must be sent explicitly — the backend's default
   * identity resolution only picks human identities, so an unsent NHI would fall
   * back to the wrong account.
   */
  isNhi?: boolean;
}

export const NO_ELIGIBLE_ACCOUNT =
  "None of your accounts meet this policy's requirements.";

export const isAppItem = (item: FlatRequestItem): boolean => {
  const sr = item.resource.selectableResource;
  return sr.type === "app" || sr.resource?.type === "app";
};

// Which account a resource's grant can land on. The backend grants onto an
// IDENTITY — your account on the resource's integration (matched on intId).
//   • integration resource → your account(s) on that integration (a choice when
//     you have more than one).
//   • app resource → there's no integration account; the backend mints an
//     app-specific identity from your EMAIL at provision time. We surface that
//     as a single, static line (no choice) so every row shows where it lands.
// A policy that says which accounts the request may land on narrows the
// integration accounts to the ones it admits (`eligible`). The server reads
// those from the same active identities, and never admits an NHI.
export const optionsForItem = (
  item: FlatRequestItem,
  me: MFMember | undefined,
  eligible?: string[],
): IdentityOption[] => {
  if (isAppItem(item)) {
    // Display-only: it's a single option, so it's never seeded into a selection
    // and the request submits with no identityId — the backend creates the
    // email-keyed app identity itself.
    return me?.email
      ? [
          {
            id: `app-${item.id}`,
            label: me.email,
            imageUrl: item.catalogItem.imageUrl || undefined,
          },
        ]
      : [];
  }

  const intId = item.resource.selectableResource.resource?.intId;
  if (!intId) return [];

  const real = (me?.activeIdentities ?? [])
    .filter((id) => id.intId === intId)
    .map((id) => ({
      id: id.id,
      label: id.username || id.displayName || id.extId,
      subtitle: id.username ? id.extId : undefined,
      imageUrl: id.intImageUrl,
    }));
  if (eligible) return real.filter((o) => eligible.includes(o.id));

  // NHIs a person owns live on the same integration but are surfaced separately
  // (backend `requestableNhis`), since ownership isn't the member_id link.
  const nhis = (me?.requestableNhis ?? [])
    .filter((id) => id.intId === intId)
    .map((id) => ({
      id: id.id,
      label: id.username || id.displayName || id.extId,
      subtitle: "Non-human identity",
      imageUrl: id.intImageUrl,
      isNhi: true,
    }));

  return [...real, ...nhis];
};

// The account a fresh row defaults to: the one that LACKS the access (the point
// of the request), else the first eligible. Empty only when there are no options.
export const defaultSelection = (opts: IdentityOption[]): string[] => {
  const preferred = opts.find((o) => !o.alreadyHasAccess) ?? opts[0];
  return preferred ? [preferred.id] : [];
};

// The accounts a row is sent for: the user's choice, less any account no longer
// offered (a role ticked since may admit fewer). A choice left with nothing is
// treated as never made, while one the user cleared themselves stays cleared.
export const resolveSelection = (
  opts: IdentityOption[],
  chosen: string[] | undefined,
): string[] => {
  if (!chosen) return defaultSelection(opts);
  const offered = chosen.filter((id) => opts.some((o) => o.id === id));
  return offered.length === 0 && chosen.length > 0
    ? defaultSelection(opts)
    : offered;
};

export interface AccountChoices {
  getOptions: (itemId: string) => IdentityOption[];
  getSelected: (itemId: string) => string[];
  problem: (itemId: string) => string | null;
}

// Rows the requester's own request on can't be sent: no account of theirs may
// be given it, or they cleared a multi-account choice. Dropped for them only.
export const unsendableForSelf = (
  items: FlatRequestItem[],
  accounts: AccountChoices,
): string[] =>
  items
    .filter(
      (it) =>
        Boolean(accounts.problem(it.id)) ||
        (accounts.getOptions(it.id).length > 1 &&
          accounts.getSelected(it.id).length === 0),
    )
    .map((it) => it.id);
